import React from 'react'
import { Check, Sparkles, FileText, Wrench, Gift } from 'lucide-react'
import { cn } from '../../lib/utils'

interface PackageCardProps {
  title: string
  price: string
  description: string
  features: string[]
  recommended?: boolean
  selected: boolean
  onClick: () => void
}

const getFeatureIcon = (feature: string) => {
  if (feature.toLowerCase().includes('snapshot') || feature.toLowerCase().includes('report')) {
    return <FileText className="w-4 h-4" style={{ color: '#145F6B' }} />
  }
  if (feature.toLowerCase().includes('maintenance') || feature.toLowerCase().includes('task')) {
    return <Wrench className="w-4 h-4" style={{ color: '#145F6B' }} />
  }
  if (feature.toLowerCase().includes('credit') || feature.toLowerCase().includes('bonus')) {
    return <Gift className="w-4 h-4" style={{ color: '#9B775D' }} />
  }
  return <Check className="w-4 h-4" style={{ color: '#145F6B' }} />
}

export const PackageCard: React.FC<PackageCardProps> = ({
  title,
  price,
  description,
  features,
  recommended = false,
  selected,
  onClick,
}) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        'border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 relative',
        selected
          ? 'border-[#9B775D] shadow-lg'
          : 'border-gray-300 hover:border-[#145F6B] hover:shadow-md',
        recommended && 'border-[#9B775D] border-3'
      )}
      style={
        selected
          ? { backgroundColor: 'rgb(246, 241, 237)', borderWidth: '3px' }
          : { backgroundColor: 'white' }
      }
    >
      {recommended && (
        <div
          className="absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
          style={{ backgroundColor: '#9B775D', color: 'white' }}
        >
          <Sparkles className="w-3 h-3" />
          Best Value
        </div>
      )}
      <h3 className="text-2xl font-semibold mb-2" style={{ color: 'rgb(15,15,15)' }}>
        {title}
      </h3>
      <div className="mb-4">
        <span className="text-3xl font-bold" style={{ color: '#9B775D' }}>
          {price}
        </span>
        <span className="text-sm ml-2" style={{ color: 'rgb(76,76,75)' }}>
          one-time
        </span>
      </div>
      <p className="mb-6 text-base leading-relaxed" style={{ color: 'rgb(76,76,75)' }}>
        {description}
      </p>
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-px flex-1" style={{ backgroundColor: '#dee2e6' }} />
          <span className="text-xs font-semibold uppercase tracking-wide px-2" style={{ color: '#145F6B', backgroundColor: 'white' }}>
            What's Included
          </span>
          <div className="h-px flex-1" style={{ backgroundColor: '#dee2e6' }} />
        </div>
        <ul className="space-y-3">
          {features.map((feature, index) => {
            const isPlus = feature.toLowerCase().includes('plus') || feature.toLowerCase().includes('everything')
            return (
              <li
                key={index}
                className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                  isPlus ? 'font-semibold' : ''
                }`}
                style={{
                  backgroundColor: isPlus ? 'rgb(246, 246, 241)' : 'transparent',
                  color: isPlus ? '#145F6B' : 'inherit',
                }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getFeatureIcon(feature)}
                </div>
                <span
                  className={`text-sm leading-relaxed ${isPlus ? 'font-semibold' : ''}`}
                  style={{ color: isPlus ? '#145F6B' : 'rgb(15,15,15)' }}
                >
                  {feature}
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

